// src/constants/checklistTemplates.js
//
// Ready-made QA checklist template shown to users who don't have any checklists
// yet, so they can start from an editable example instead of a blank list.
// Questions intentionally omit `id` — fresh uuids are minted when the template
// is instantiated into a real checklist (see handleCreateFromTemplate).
//
// Structure mirrors the two-tier prompt design:
//   - `context`  = customer-owned reference knowledge (background + workflow) +
//                  a global scoring stance. Generic judge rules (ASR tolerance,
//                  "don't penalize situations that didn't occur", quote-on-violation,
//                  etc.) are NOT repeated here — the backend owns them.
//   - `guidance` = the rubric for one specific question (good / triggers / score bands).

export const CALL_CENTER_QA_TEMPLATE = {
  name: 'Զանգերի որակի ստուգում',
  description: 'Օպերատորի և հաճախորդի զրույցի որակը գնահատելու ձևանմուշ։',
  context: `Ընկերության նկարագրություն․ Մենք հեռահաղորդակցության և ինտերնետ ծառայություններ մատուցող ընկերության հաճախորդների սպասարկման կենտրոն ենք։ Օպերատորները հիմնականում սպասարկում են մուտքային զանգեր գործող բաժանորդներից և պոտենցիալ հաճախորդներից։

Հաճախորդների տիպիկ հարցումները․ վճարումներ և հաշիվներ; տեխնիկական խնդիրներ (ինտերնետի անջատում, ցածր արագություն, սարքավորման կարգավորում); ծառայությունների միացում կամ փոփոխություն; բողոքներ և վիճելի իրավիճակներ։

Ընդունված workflow․
1. Ողջունել և ներկայանալ (անուն և ընկերություն)։
2. Նույնականացնել բաժանորդին (անուն, պայմանագրի կամ հեռախոսահամար)։
3. Ճշտել և վերաձևակերպել խնդիրը՝ համոզվելու ճիշտ ընկալման մեջ։
4. Առաջարկել լուծում՝ ընկերության սցենարներով; տեղում չլուծվելու դեպքում գրանցել հայտ կամ փոխանցել համապատասխան բաժին՝ նշելով սպասվող ժամկետը։
5. Ամփոփել ձեռնարկված քայլերը և ստանալ հաճախորդի հաստատումը։
6. Քաղաքավարի ավարտել զրույցը (շնորհակալություն, հրաժեշտ)։

Գնահատման մոտեցում (stance)․ Գնահատիր օբյեկտիվ և չափավոր խստությամբ. կասկածի դեպքում հիմնվիր transcript-ի հստակ ապացույցների վրա։`,
  questions: [
    {
      text: 'Արդյո՞ք օպերատորը ողջունեց հաճախորդին և ներկայացավ զրույցի սկզբում։',
      type: 'yes_no_partially',
      guidance: `Լավ՝ օպերատորը ողջունում է և ներկայանում (անուն և/կամ ընկերության անուն)։
Խախտում՝ ողջույն ընդհանրապես չկա, կամ սկսում է ուղիղ գործից։
Մասնակի՝ ողջունում է, բայց անունը/ընկերությունը բացակայում է, կամ ասում է միայն «Ալո՞»/«Այո՞»։
Կիրառելիություն՝ եթե transcript-ի սկիզբը ակնհայտ կտրված է՝ cannot_tell։`,
    },
    {
      text: 'Արդյո՞ք օպերատորը ճշտեց և ճիշտ հասկացավ հաճախորդի խնդիրը կամ հարցումը։',
      type: 'yes_no_partially',
      guidance: `Լավ՝ ճշտում/վերաձևակերպում է խնդիրը և տալիս բովանդակային արձագանք։
Խախտում՝ վարում է միակողմանի խոսակցություն կամ անտեսում հաճախորդի տված տեղեկությունը։
Մասնակի՝ հասկանում է, բայց հաճախորդը ստիպված կրկնում է, կամ հարցնում է մի բան, որին արդեն պատասխանվել է։`,
    },
    {
      text: 'Արդյո՞ք օպերատորը խոսեց քաղաքավարի, հանգիստ և պրոֆեսիոնալ տոնով ամբողջ զրույցի ընթացքում։',
      type: 'yes_no_partially',
      guidance: `Լավ՝ խոսքը քաղաքավարի և պրոֆեսիոնալ է, առանց կոպիտ, մեղադրող կամ պարտադրող ձևակերպումների։
Խախտում՝ վիրավորական, կոպիտ կամ ակնհայտ նյարդայնացնող արտահայտություններ (օր.՝ «Հանգստացեք», «Դուք պետք է», «Ես ձեզ ասացի»)։
Մասնակի՝ ընդհանուր առմամբ քաղաքավարի, բայց կա չոր, անտարբեր կամ պարտադրող հատված։
Ուշադրություն՝ գնահատվում է միայն բառերի քաղաքավարությունը. ձայնի տոնը transcript-ով հնարավոր չէ որոշել։`,
    },
    {
      text: 'Արդյո՞ք օպերատորն առաջարկեց խնդրի լուծում կամ հստակ բացատրեց հաջորդ քայլերը։',
      type: 'yes_no_partially',
      guidance: `Լավ՝ առաջարկում է լուծում, բացատրություն, հաջորդ քայլ կամ համապատասխան ուղղորդում։
Խախտում՝ ակնհայտ խուսափում է օգնելուց կամ պատասխանատվությունից, կամ չի փոխանցում հարցումը համապատասխան բաժին, երբ դա պահանջվում էր։
Մասնակի՝ առաջարկում է լուծում, բայց թերի, առանց հստակ քայլերի, կամ առանց խնդիրը լիարժեք հասկանալու։`,
    },
    {
      text: 'Արդյո՞ք օպերատորը պատշաճ ավարտեց զրույցը (ամփոփում, շնորհակալություն, հրաժեշտ)։',
      type: 'yes_no_partially',
      guidance: `Լավ՝ ամփոփում, շնորհակալություն և քաղաքավարի հրաժեշտ։
Խախտում՝ անջատում է զանգը առանց հրաժեշտի, երբ հաճախորդը դեռ խոսում է։
Մասնակի՝ ավարտում է, բայց առանց լիարժեք ամփոփման կամ շնորհակալության՝ ոչ կոպիտ ձևով։
Կիրառելիություն՝ եթե transcript-ը կտրված է և ավարտը չի երևում՝ cannot_tell։`,
    },
    {
      text: 'Գնահատիր զրույցի ընդհանուր որակը հաճախորդի սպասարկման տեսանկյունից։',
      type: 'grade_1_10',
      guidance: `Գնահատիր սպասարկման ընդհանուր որակը՝ հաշվի առնելով վերը նշված workflow-ին համապատասխանությունը։
Սանդղակ՝ 9–10 = պրոֆեսիոնալ, ամբողջական սպասարկում առանց էական թերությունների; 5–8 = ընդունելի, բայց մի քանի շեղումներով; 1–4 = լուրջ թերություններ կամ խախտումներ։
10-ից ցածր գնահատականը հիմնավորիր զրույցի կոնկրետ մեջբերումով։`,
    },
  ],
};
